import React, { useState } from "react";
import PropTypes from "prop-types";
import { downloadFileFromUrl } from "@utils/fileDownload";
import PDFViewer from "./PDFViewer";
import OfficePreview from "./OfficePreview";
import { CloseIcon, ZoomInIcon, ZoomOutIcon, DownloadIcon } from "./PreviewIcons";
import styles from "./DocumentPreview.styled";

const DocumentPreview = ({ document: documentData, onClose }) => {
  const [zoom, setZoom] = useState(1);
  const [error, setError] = useState(null);

  const handleZoomIn = () => setZoom((prev) => prev + 0.1);
  const handleZoomOut = () =>
    setZoom((prev) => (prev > 0.2 ? prev - 0.1 : 0.1));

  const handleDownload = () => {
    if (documentData.downloadLink) {
      downloadFileFromUrl(documentData.downloadLink, documentData.fileName);
    }
  };

  // Get file extension from document
  const getFileExtension = (filename) => {
    return filename.toLowerCase().split(".").pop();
  };

  const fileExtension = getFileExtension(
    documentData.fileName || documentData.downloadLink || ""
  );

  // Categorize document types
  const isImage = ["jpg", "jpeg", "png", "gif", "bmp", "webp", "svg"].includes(
    fileExtension
  );
  const isPDF = fileExtension === "pdf";
  const isOfficeDoc = ["doc", "docx", "xls", "xlsx", "ppt", "pptx", "eml"].includes(
    fileExtension
  );
  const isText = ["txt", "csv", "json", "xml", "html"].includes(fileExtension);

  // Get the appropriate URL for viewing
  const getViewUrl = () => {
    // Use viewLink if available (from new API), otherwise fallback to downloadLink
    const url = documentData.viewLink || documentData.downloadLink;
    if (!url) return "";

    return url.replace(/^http:/, "http:");
  };

  const secureViewLink = getViewUrl();

  return (
    <div style={styles.overlay}>
      <div style={styles.content}>
        <div style={styles.header}>
          <button
            onClick={handleDownload}
            style={styles.iconButton}
            title="Download"
          >
            <DownloadIcon />
          </button>
          {isImage && (
            <>
              <button onClick={handleZoomIn} style={styles.iconButton}>
                <ZoomInIcon />
              </button>
              <button onClick={handleZoomOut} style={styles.iconButton}>
                <ZoomOutIcon />
              </button>
            </>
          )}
          <button
            onClick={onClose}
            style={{ ...styles.iconButton, ...styles.closeButton }}
          >
            <CloseIcon />
          </button>
        </div>
        <div style={styles.documentContainer}>
          {error ? (
            <div style={styles.errorContainer}>
              <h3>Unable to preview document</h3>
              <p>{error}</p>
              <button
                onClick={handleDownload}
                style={styles.downloadButton}
              >
                Download Document
              </button>
            </div>
          ) : isImage ? (
            <img
              src={secureViewLink}
              alt="document preview"
              style={{ ...styles.document, transform: `scale(${zoom})` }}
              onError={() =>
                setError(
                  "Unable to load image. The file may be corrupted or in an unsupported format."
                )
              }
            />
          ) : isPDF ? (
            <PDFViewer
              url={secureViewLink}
              onError={() =>
                setError("Unable to load PDF. Please try downloading the file.")
              }
            />
          ) : isOfficeDoc ? (
            <OfficePreview
              url={secureViewLink}
              fileExtension={fileExtension}
              onError={(msg) =>
                setError(msg || "Unable to load Office document preview. Please download the file.")
              }
            />
          ) : isText ? (
            <iframe
              src={secureViewLink}
              title="text document preview"
              style={{ ...styles.document, transform: `scale(${zoom})` }}
              onError={() => setError("Unable to load text document.")}
            />
          ) : (
            <div style={styles.unsupportedContainer}>
              <div style={styles.unsupportedMessage}>
                <h3>Preview Not Available</h3>
                <p>
                  Preview is not supported for {fileExtension.toUpperCase()}{" "}
                  files.
                </p>
                <button
                  onClick={handleDownload}
                  style={styles.downloadButton}
                >
                  Download File
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

DocumentPreview.propTypes = {
  document: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default DocumentPreview;
